import type { FormId } from "@@/shared/form/form-and-field-id";

import { PriceEdit_V2_WithLegacyPadding } from "@@/shared/text/price-text-edit_v2";
import { useTranslate } from "@@/translations/use-translate";
import type { DeepKeysOf, Price, Translatable } from "@towni/common";
import { isPriceEqual, zeroPrice } from "@towni/common";
import { useState } from "react";
import type { infer as InferZodType, ZodSchema } from "zod";
import type { Form3ErrorMessage } from "./form3.error-message";
import { useForm3Field } from "./form3.use-form-field";

type ValueType = Price;

type Props<FormData, FieldId extends DeepKeysOf<FormData>> = {
    readonly className?: string;
    readonly fieldId: FieldId;
    readonly formId?: FormId;
    readonly label?: Translatable;
};

const createForm3PriceInputComponent = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
>() => {
    const Form3PriceInput = <
        FieldId extends DeepKeysOf<FormData> = DeepKeysOf<FormData>,
    >(
        props: Props<FormData, FieldId>,
    ) => {
        const translate = useTranslate();
        const [errors, setErrors] = useState<Form3ErrorMessage[]>([]);
        const field = useForm3Field<
            FormSchema,
            InferZodType<FormSchema>,
            FieldId,
            ValueType
        >({
            fieldId: props.fieldId,
            formId: props.formId,
        });

        const value = field.value ?? zeroPrice;

        return (
            <PriceEdit_V2_WithLegacyPadding
                vat={value.vat}
                required={field.isRequired}
                label={props.label}
                inputExclVat={false}
                // Sets the initial price to the field's initial value if it exists, otherwise it uses the current value
                //This because if a dynamic price field is added the initial value will be undefined as it is not known in the intial data
                initialPrice={field.initialValue ?? value}
                onBlur={() => {
                    field.setTouched(true);
                    setErrors(field.validate());
                }}
                errorMessage={errors.map(error => translate(error)).join(", ")}
                onChange={newPrice => {
                    field.setValue(newPrice ?? zeroPrice);
                    field.setDirty(
                        isPriceEqual(newPrice, field.initialValue || undefined),
                    );
                    field.setTouched(true);
                    setErrors(field.validate());
                }}
            />
        );
    };
    return Form3PriceInput;
};

export { createForm3PriceInputComponent };
